import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Pencil, AlertCircle, ShieldAlert } from "lucide-react";
import { API } from "../../config";
import { PhoneInput } from "./PhoneInput";
import { CountryCombobox } from "./CountryCombobox";
import { getPrefixByCountry, normalizeCountryCode } from "../../constants/countries";
import { GdprAdminPanel } from "../admin/GdprAdminPanel";
import { ContractTemplateDiagnosticsWidget } from "../admin/ContractTemplateDiagnosticsWidget";
import { LastEmailWidget } from "../dealer/LastEmailWidget";
import { useAuth } from "../../context/AuthContext";
import { getEditCustomerT, getEditCustomerLang } from "../../translations/editCustomerModal";

const ADMIN_OVERRIDE_ROLES = new Set(["super_admin", "admin", "it"]);

// Fallback dropdown options used when GET /api/service-types is unreachable.
// Must stay in sync with the server-side _ACTIVE_DURATIONS_BY_OEM in
// services/service_type_display.py. CR Zorzutti 03/10/2026 — Track & Trace
// GPS rename. Internal key stays stable; only the visible label changes.
const SERVICE_TYPE_FALLBACK = [
  { value: "LoJack Tracker Passive 1", label: "Track & Trace GPS 1" },
  { value: "LoJack Tracker Passive 3", label: "Track & Trace GPS 3" },
  { value: "LoJack Tracker Passive 4", label: "Track & Trace GPS 4" },
];

/**
 * EditCustomerModal — Allows editing of a customer's basic data.
 *
 * Hard rules — the form becomes READ-ONLY when EITHER:
 *   1. ``contract_signed === true`` — the data is crystallised into
 *      the signed PDF and must never be mutated (legal / GDPR).
 *   2. ``lock_status.locked === true`` — the customer has started
 *      filling in their own data (Mara + Zorzutti CR 19/02/2026):
 *      accept-terms, GDPR preferences, personal-data submit. From
 *      this moment the dealer must not modify the record;
 *      admin / IT users keep full access via the same modal mounted
 *      from /customers (their JWT bypasses the backend 423 check).
 *
 * The two locks render distinct banners so the dealer immediately
 * understands WHY they cannot edit (legal-lock vs customer-action-lock).
 */
export const EditCustomerModal = ({ customer, onClose, onSuccess, dealerToken }) => {
  const { user } = useAuth();
  const isAdmin = !!(user && ADMIN_OVERRIDE_ROLES.has(user.role));
  // Resolve dealer/admin language → modal labels & banners.
  const t = getEditCustomerT();

  const contractLocked = !!customer.contract_signed;
  // lock_status is sent by the dealer GET endpoint with banner copy
  // pre-resolved in the dealer's country. When the modal is opened
  // from the admin page, lock_status is undefined → no banner.
  const selfCompilationLock = customer.lock_status || null;
  const showSelfCompilationBanner = !!(
    selfCompilationLock && selfCompilationLock.locked && !contractLocked
  );
  const locked = contractLocked || showSelfCompilationBanner;

  // Admin override (Fabrizio CR 23/06/2026): when an admin/IT/super_admin
  // opens this modal on a locked customer, they can opt-in to override
  // by toggling this state. The backend already accepts the PUT and
  // records every override in `customer_admin_override_logs` with a
  // full before/after diff. Dealers and sellers never see this option.
  const [adminOverride, setAdminOverride] = useState(false);
  const editingDisabled = locked && !(isAdmin && adminOverride);
  // Fix E (03/03/2026): missing-mandatory keys lifted from the diagnostics
  // widget so we can flag the matching form labels with a red asterisk.
  const [missingFields, setMissingFields] = useState([]);
  const isMissing = (key) => missingFields.includes(key);
  const [form, setForm] = useState({
    first_name: customer.first_name || "",
    last_name: customer.last_name || "",
    email: customer.email || "",
    phone: customer.phone || "",
    phone_prefix: customer.phone_prefix || getPrefixByCountry(customer.country || "IT"),
    // Fabrizio bug 06/07/2026 pt.5 — normalize ISO → Legacy at form init so
    // that a legacy DB record with ``country="GB"`` correctly preselects
    // the "UK" option in the dropdown (otherwise ``<select value="GB">``
    // doesn't match any option and appears blank / falls back to the 1st).
    country: normalizeCountryCode(customer.country) || "IT",
    vehicle_brand: customer.vehicle_brand || "",
    vehicle_model: customer.vehicle_model || "",
    vehicle_plate: customer.vehicle_plate || "",
    vehicle_vin: customer.vehicle_vin || "",
    service_type: customer.service_type || "LoJack Tracker Passive 1",
  });
  const [saving, setSaving] = useState(false);

  // Dropdown "Tipo Servizio" — OEM-aware labels loaded from backend
  // (/api/service-types). Falls back to the hardcoded Toyota pilot list
  // if the endpoint is unreachable, so the modal never breaks.
  // CR Zorzutti 03/10/2026 — Track & Trace GPS rename.
  const [serviceTypeOptions, setServiceTypeOptions] = useState(SERVICE_TYPE_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/service-types`)
      .then(res => {
        if (cancelled) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setServiceTypeOptions(res.data);
        }
      })
      .catch(() => {
        // Silent fallback: SERVICE_TYPE_FALLBACK already set, no UX impact.
      });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingDisabled) return;
    try {
      setSaving(true);
      await axios.put(`${API}/customers/${customer.id}`, form);
      toast.success(t.saved);
      onSuccess();
    } catch (err) {
      // Special case: backend returns 423 LOCKED when the customer
      // has started self-compilation since the modal was opened
      // (race: another tab / the actual customer triggered the lock
      // between page load and submit). We surface the localised
      // banner copy from the response so the dealer sees the same
      // bilingual message as the standalone banner above.
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 423 && detail && typeof detail === "object") {
        toast.error(detail.banner_body || detail.message || t.edit_not_allowed);
        return;
      }
      // 409 Conflict — VIN already enrolled by another customer.
      if (status === 409 && detail && typeof detail === "object" && detail.error === "vin_already_enrolled") {
        const ownerName = detail.existing_customer_name || detail.existing_customer_email || t.vin_dupe_owner;
        toast.error(
          t.vin_dupe_msg.replace("{vin}", detail.vin).replace("{owner}", ownerName),
          { duration: 7000 }
        );
        return;
      }
      toast.error(detail || err.response?.data?.detail || t.save_error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
      style={{ width: "100vw", height: "100vh", top: 0, left: 0 }}
      onClick={onClose}
      data-testid="edit-customer-modal-overlay"
    >
      <div
        className="bg-[#1A1A1A] rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-gray-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-customer-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 flex-wrap">
            <Pencil size={18} className="text-lojack-red" />
            <span>{t.title}</span>
            {(() => {
              const isBusiness = (customer.customer_type || 'private') === 'business';
              const cls = isBusiness
                ? 'bg-lojack-clause-500/15 text-lojack-clause-300 border-lojack-clause-500/40'
                : 'bg-sky-500/15 text-sky-300 border-sky-500/40';
              const dot = isBusiness ? 'bg-lojack-clause-400' : 'bg-sky-400';
              const label = isBusiness ? t.badge_business : t.badge_private;
              return (
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${cls}`}
                  data-testid={`edit-customer-type-badge-${isBusiness ? 'business' : 'private'}`}
                  title={isBusiness ? t.badge_business_tt : t.badge_private_tt}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                  {label}
                </span>
              );
            })()}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none"
            data-testid="edit-customer-close"
          >
            &times;
          </button>
        </div>

        {contractLocked && (
          <div className="mx-5 mt-4 bg-lojack-clause-500/10 border border-lojack-clause-500/30 rounded-xl p-4 flex items-start gap-3">
            <Lock size={20} className="text-lojack-clause-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-lojack-clause-200">
              <p className="font-medium mb-1">{t.contract_locked_title}</p>
              <p className="text-lojack-clause-200/80">{t.contract_locked_body}</p>
            </div>
          </div>
        )}

        {showSelfCompilationBanner && (
          <div
            className="mx-5 mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3"
            data-testid="customer-self-compilation-lock-banner"
          >
            <AlertCircle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              <p
                className="font-medium mb-1"
                data-testid="self-compilation-lock-title"
              >
                {selfCompilationLock.banner_title}
              </p>
              <p
                className="text-amber-200/80"
                data-testid="self-compilation-lock-body"
              >
                {selfCompilationLock.banner_body}
              </p>
            </div>
          </div>
        )}

        {/* Admin override toggle — visible only to super_admin/admin/it
            when the customer is locked (post-signature OR self-compilation).
            Backend already authorises the PUT for these roles and persists
            every override in `customer_admin_override_logs`.

            03/03/2026 layout fix (Fabrizio CR): when override is ACTIVE
            the banner collapses to a single compact row so the form has
            room to render the actual fields. The verbose explainer is
            kept for the "Abilita override" state where the admin needs
            to read what the action entails. */}
        {isAdmin && locked && (
          <>
            {!adminOverride ? (
              <div
                className="mx-5 mt-4 rounded-xl p-4 flex items-start gap-3 border bg-blue-500/10 border-blue-500/30"
                data-testid="admin-override-banner"
              >
                <ShieldAlert size={20} className="text-blue-300 flex-shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <p
                    className="font-medium mb-1 text-blue-100"
                    data-testid="admin-override-banner-title"
                  >
                    {t.override_available_title}
                  </p>
                  <p className="text-blue-100/80 mb-3">
                    {t.override_disabled_body.replace("{role}", user?.role || "")}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAdminOverride(true)}
                    className="text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-md transition-colors bg-blue-700 hover:bg-blue-600 text-white"
                    data-testid="admin-override-enable"
                  >
                    {t.override_enable}
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="mx-5 mt-4 rounded-lg px-3 py-2 flex items-center gap-2 border bg-orange-500/15 border-orange-500/50"
                data-testid="admin-override-banner"
              >
                <ShieldAlert size={16} className="text-orange-300 flex-shrink-0" />
                <p
                  className="text-xs text-orange-100 flex-1 min-w-0 truncate"
                  data-testid="admin-override-banner-title"
                  title={t.override_active_title}
                >
                  <span className="font-semibold">🛡 Override ATTIVO</span>
                  {user?.email && (
                    <span className="text-orange-100/70"> · {user.email}</span>
                  )}
                  <span className="text-orange-100/60"> · modifiche tracciate</span>
                </p>
                <button
                  type="button"
                  onClick={() => setAdminOverride(false)}
                  className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded transition-colors bg-orange-700 hover:bg-orange-600 text-white flex-shrink-0"
                  data-testid="admin-override-disable"
                >
                  {t.override_disable}
                </button>
              </div>
            )}
          </>
        )}

        {/* Last-email widget — dealer-only. Surfaces the most recent
            email dispatched to this customer (Zorzutti CR 31/07/2026)
            so the sales rep can tell at-a-glance whether the welcome
            went out, when the sollecito fired, and if the customer
            actually engaged with the wallet link. Open/click signals
            derive from wallet events (mock) until AWS SES event
            publishing lands. Hidden when the modal is opened from
            the admin page (no dealerToken). */}
        {dealerToken && (
          <LastEmailWidget
            customerId={customer.id}
            dealerToken={dealerToken}
            t={t}
          />
        )}


        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-auto p-5 space-y-4"
          data-testid="edit-customer-form"
        >
          <div className="grid grid-cols-2 gap-3">
            <div data-field-key="customer.first_name">
              <label className="text-gray-400 text-xs">
                {t.first_name}
                {isMissing("customer.first_name") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                required
                data-testid="edit-first-name"
              />
            </div>
            <div data-field-key="customer.last_name">
              <label className="text-gray-400 text-xs">
                {t.last_name}
                {isMissing("customer.last_name") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                required
                data-testid="edit-last-name"
              />
            </div>
          </div>

          <div data-field-key="customer.email">
            <label className="text-gray-400 text-xs">
              {t.email}
              {isMissing("customer.email") && (
                <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
              )}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={editingDisabled}
              className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              required
              data-testid="edit-email"
            />
          </div>

          <div data-field-key="customer.phone">
            <label className="text-gray-400 text-xs">
              {t.phone}
              {isMissing("customer.phone") && (
                <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
              )}
            </label>
            <div className="mt-1">
              <PhoneInput
                prefix={form.phone_prefix}
                number={form.phone}
                onChange={({ prefix, number }) =>
                  setForm({ ...form, phone_prefix: prefix, phone: number })
                }
                disabled={editingDisabled}
                testId="edit-phone"
                customerCountry={form.country}
                uiLang={getEditCustomerLang()}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div data-field-key="customer.country">
              <label className="text-gray-400 text-xs">
                {t.country}
                {isMissing("customer.country") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              {/* Residence country selector — Fabrizio 24/07/2026:
                  upgrade da <select> di 18 paesi curati a
                  CountryCombobox typeahead con tutti i 193 paesi ISO.
                  Ogni cambio ripopola anche il phone_prefix per
                  restare consistente con la selezione. */}
              <CountryCombobox
                value={form.country}
                onChange={(code) => {
                  if (!code) return;
                  setForm({
                    ...form,
                    country: code,
                    phone_prefix: getPrefixByCountry(code),
                  });
                }}
                disabled={editingDisabled}
                required
                testId="edit-country"
              />
            </div>
            <div data-field-key="customer.service_type">
              <label className="text-gray-400 text-xs">
                {t.service}
                {isMissing("customer.service_type") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <select
                value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="edit-service-type"
              >
                {serviceTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} style={{ backgroundColor: "#111" }}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div data-field-key="vehicle.brand">
              <label className="text-gray-400 text-xs">
                {t.vehicle_brand}
                {isMissing("vehicle.brand") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <input
                value={form.vehicle_brand}
                onChange={(e) => setForm({ ...form, vehicle_brand: e.target.value })}
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="edit-vehicle-brand"
              />
            </div>
            <div data-field-key="vehicle.model">
              <label className="text-gray-400 text-xs">
                {t.vehicle_model}
                {isMissing("vehicle.model") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <input
                value={form.vehicle_model}
                onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="edit-vehicle-model"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div data-field-key="vehicle.vin">
              <label className="text-gray-400 text-xs">
                {t.vin}
                {isMissing("vehicle.vin") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <input
                value={form.vehicle_vin}
                onChange={(e) => setForm({ ...form, vehicle_vin: e.target.value.toUpperCase() })}
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed uppercase"
                data-testid="edit-vin"
              />
            </div>
            <div data-field-key="vehicle.plate">
              <label className="text-gray-400 text-xs">
                {t.plate}
                {isMissing("vehicle.plate") && (
                  <span className="text-lojack-clause-400 ml-1" title={t.missing_v2_tooltip}>*</span>
                )}
              </label>
              <input
                value={form.vehicle_plate}
                onChange={(e) =>
                  setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })
                }
                disabled={editingDisabled}
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed uppercase"
                data-testid="edit-plate"
              />
            </div>
          </div>

          {!editingDisabled && (
            <p className="text-gray-500 text-xs flex items-start gap-1.5 pt-2">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
              {adminOverride ? t.override_note : t.unlocked_note}
            </p>
          )}

          {/* Fix A (03/03/2026): Diagnostica template + GDPR panel are
              now INSIDE the scrollable form so the footer (Annulla / Salva)
              stays anchored to the modal bottom. Previously they rendered
              AFTER the footer which split the modal into two awkward halves. */}
          <ContractTemplateDiagnosticsWidget
            customer={customer}
            onMissingFieldsChange={setMissingFields}
          />
          <GdprAdminPanel customer={customer} onChange={onSuccess} />
        </form>

        <div className="flex items-center justify-between p-5 border-t border-gray-700 bg-[#1A1A1A]">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="edit-customer-cancel"
          >
            {editingDisabled ? t.close : t.cancel}
          </button>
          {!editingDisabled && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`flex items-center gap-2 disabled:opacity-50 ${
                adminOverride
                  ? "px-5 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-lg transition-colors"
                  : "btn-lojack"
              }`}
              data-testid={adminOverride ? "edit-customer-save-override" : "edit-customer-save"}
            >
              {saving ? <div className="spinner-small" /> : (adminOverride ? <ShieldAlert size={16} /> : <Pencil size={16} />)}
              {saving
                ? t.saving
                : (adminOverride ? t.save_override : t.save)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditCustomerModal;
